import i18n from 'i18next';
import { Dispatch } from 'redux';
import {
  deleteRequest,
  getRequest,
  postRequest,
  putRequest
} from 'src/store/api-handler';
import {
  InstrumentDto,
  InstrumentEditDto
} from 'src/store/instruments/instrument';
import { alertError, alertSuccess } from '../alerts/alerts.actions';
import { requestBegin, requestEnd } from '../requests/requests.actions';

export enum InstrumentTypes {
  FetchAll = 'instruments/FetchAll',
  Add = 'instruments/Add',
  Update = 'instruments/Update',
  Delete = 'instruments/Delete',
  Failure = 'instruments/Failure'
}

export type InstrumentAction =
  | { type: InstrumentTypes.FetchAll; instruments: InstrumentDto[] }
  | { type: InstrumentTypes.Add; instrument: InstrumentDto }
  | { type: InstrumentTypes.Update; instrument: InstrumentDto }
  | { type: InstrumentTypes.Delete; instrument: InstrumentDto }
  | { type: InstrumentTypes.Failure; errors: any };

export const actionCreators = {
  fetchInstruments: (instruments: InstrumentDto[]): InstrumentAction => ({
    type: InstrumentTypes.FetchAll,
    instruments
  }),
  addInstrument: (instrument: InstrumentDto): InstrumentAction => ({
    type: InstrumentTypes.Add,
    instrument
  }),
  updateInstrument: (instrument: InstrumentDto): InstrumentAction => ({
    type: InstrumentTypes.Update,
    instrument
  }),
  deleteInstrument: (instrument: InstrumentDto): InstrumentAction => ({
    type: InstrumentTypes.Delete,
    instrument
  }),
  instrumentRequestFailure: (errors: any): InstrumentAction => ({
    type: InstrumentTypes.Failure,
    errors
  })
};

export const fetchInstruments = () => {
  return async (dispatch: Dispatch) => {
    dispatch(requestBegin(InstrumentTypes.FetchAll));
    const { ok, data, error } = await getRequest<InstrumentDto[]>(
      '/instruments'
    );
    dispatch(requestEnd(InstrumentTypes.FetchAll));
    return ok
      ? dispatch(actionCreators.fetchInstruments(data))
      : dispatch(actionCreators.instrumentRequestFailure(error));
  };
};

export const createInstrument = (instrument: InstrumentEditDto) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await postRequest('/instruments', instrument);

    if (ok) {
      return dispatch(alertSuccess(i18n.t('instrument.created')));
    }

    dispatch(alertError(error?.errors?.detail ?? i18n.t('create.failed')));
    return dispatch(actionCreators.instrumentRequestFailure(error));
  };
};

export const editInstrument = (id: number, instrument: InstrumentEditDto) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await putRequest(`/instruments/${id}`, instrument);

    if (ok) {
      return dispatch(alertSuccess(i18n.t('instrument.updated')));
    }

    dispatch(alertError(error?.errors?.detail ?? i18n.t('save.failed')));
    return dispatch(actionCreators.instrumentRequestFailure(error));
  };
};

export const deleteInstrument = (id: number, instrument: InstrumentDto) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await deleteRequest(`/instruments/${id}`, instrument);

    if (ok) {
      dispatch(alertSuccess(i18n.t('instrument.deleted')));
      return dispatch(actionCreators.deleteInstrument(instrument));
    }
    dispatch(alertError(error?.errors?.detail ?? i18n.t('remove.failed')));
    return dispatch(actionCreators.instrumentRequestFailure(error));
  };
};
